import React, { useCallback, useEffect, useState } from "react";
import { balanceToneClass } from '../../../lib/helpers/balance';
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";

/**
 * Extracto del monedero del socio (petición Kong Club, 14-ago): cada recarga,
 * retirada y consumo con saldo, con el saldo resultante al lado — como un
 * banco. Datos de GET /v1/wallet-statement/{user_id} (reconstruidos hacia
 * atrás desde el saldo actual; las ventas anuladas no aparecen).
 */

const TIPO_LABEL = {
  add_cash: "Recarga",
  refund: "Retirada de saldo",
  purchase: "Consumo",
};

const PAGO_LABEL = {
  cash: "efectivo",
  card: "tarjeta",
  transfer: "transferencia",
};

function eur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

function fecha(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function WalletStatement({ token, venueID, clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStatement = useCallback(async () => {
    if (!token || !venueID || !clientId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/v1/wallet-statement/${clientId}?venue_id=${venueID}&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo cargar el extracto del monedero");
      setData(await res.json());
    } catch (e) {
      setError(e?.message || "Error al cargar el extracto");
    } finally {
      setLoading(false);
    }
  }, [token, venueID, clientId]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  if (loading) {
    return <div className="py-12 text-center text-[var(--wa-muted-foreground)]">Cargando extracto…</div>;
  }
  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>;
  }
  if (!data) return null;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between rounded-xl border border-[var(--wa-border)] bg-[var(--wa-card-muted)] px-4 py-3">
        <span className="text-sm text-[var(--wa-muted-foreground)]">Saldo actual</span>
        <span className={`text-lg font-bold ${balanceToneClass(data.current_balance, "text-[var(--foreground)]")}`}>
          {eur(data.current_balance)}
        </span>
      </div>

      {data.movimientos.length === 0 ? (
        <div className="py-10 text-center text-sm text-[var(--wa-muted-foreground)]">
          Sin movimientos de monedero en el histórico.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--wa-border)]">
          <table className="w-full text-sm">
            {/* Compacta a propósito: es un listado largo dentro de la ficha. */}
            <TableHead>
              <Th className="px-3 py-2.5">Fecha</Th>
              <Th className="px-3 py-2.5">Movimiento</Th>
              <Th align="right" className="px-3 py-2.5">Importe</Th>
              <Th align="right" className="px-3 py-2.5">Saldo</Th>
            </TableHead>
            <tbody>
              {data.movimientos.map((m) => (
                <Tr key={m.transaction_id}>
                  <Td className="whitespace-nowrap px-3 py-2.5 text-[var(--muted-foreground)]">{fecha(m.timestamp)}</Td>
                  <Td className="px-3 py-2.5 text-[var(--foreground)]">
                    {TIPO_LABEL[m.tipo] || m.tipo}
                    {m.tipo === "add_cash" && m.payment_method && (
                      <span className="text-[var(--muted-foreground)]"> ({PAGO_LABEL[m.payment_method] || m.payment_method})</span>
                    )}
                  </Td>
                  {/* Entra y sale: esto no es paleta categórica, es signo. Los tokens
                      del sistema lo dicen igual de bien que el rojo y el verde sueltos. */}
                  <Td align="right" className={`px-3 py-2.5 font-medium ${m.amount < 0 ? "text-[var(--destructive)]" : "text-[var(--success)]"}`}>
                    {m.amount > 0 ? "+" : ""}
                    {eur(m.amount)}
                  </Td>
                  <Td align="right" className="px-3 py-2.5 font-semibold text-[var(--foreground)]">{eur(m.balance_after)}</Td>
                </Tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between px-1 text-xs text-[var(--wa-muted-foreground)]">
        <span>
          {data.has_more ? "Saldo anterior (hay movimientos más antiguos)" : "Saldo inicial"}
        </span>
        <span className="font-semibold">{eur(data.saldo_anterior)}</span>
      </div>
    </div>
  );
}
